import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { memo, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import FeedBanner from '@comp/FeedBanner/FeedBanner';
import Button from '@base/Button/Button';
import { useSelectorToStore } from '@hooks/reduxHooks';
import useSubscriptionExpired from '@hooks/useSubscriptionExpired';
import { Context as PurchaseTypeContext } from '@context/PurchaseTypeContext';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import { CHECKOUT_STARTED } from '@constants/segment';
import segmentStorage from '@events/segmentStorage';
import { fetchPartPaymentStatus } from '@actions/checkout';
import pushRoute from '@utils/pushRoute';
import { Spacings } from '@styles/index';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import { getCheckPartPaymentStatus } from '@api/selectors/auth';

const Banner = styled(FeedBanner)`
  padding-left: ${Spacings.SPACING_10B};
  @media only screen and (max-width: 47.95em) {
    padding-left: 16px;
  }
`;

const FeedButton = styled(Button)`
  grid-row: span 2;
  @media only screen and (max-width: 47.95em) {
    margin-top: 16px;
    width: 100%;
  }
`;

const PreSubscriptionBanner = ({ goalUID, title, content, imageProps }) => {
  const purchaseType = useContext(PurchaseTypeContext);
  const dispatch = useDispatch();
  const goalInfo = useSelectorToStore(
    ({ topologies }) => topologies.topologyDetails[goalUID]
  );

  const isLoggedIn = useSelectorToStore(({ auth }) => auth.isLoggedIn);
  const { accessControl } = useAccessControl(goalUID);
  const checkPartPaymentStatus = getCheckPartPaymentStatus(accessControl);

  useEffect(() => {
    if (isLoggedIn && checkPartPaymentStatus) {
      dispatch(fetchPartPaymentStatus(goalUID));
    }
  }, [dispatch, goalUID, isLoggedIn, checkPartPaymentStatus]);

  const partPaymentStatus = useSelectorToStore(
    ({ checkout }) => checkout.partPaymentStatus?.[goalUID]
  );

  const isSubscriptionExpired = useSubscriptionExpired(goalUID);
  const getOrRenewSubscrioption = () => {
    if (!goalInfo) return;
    segment.track(
      CHECKOUT_STARTED,
      {
        goal_uid: goalUID,
        goal_name: goalInfo.name,
        is_subscription_active: false,
        purchase_type: purchaseType,
        is_upgrade: false,
        last_primary_source: segmentStorage.getItem('lps')
      },
      evtNameCategoryMap.BOTH
    );
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalInfo.goalSlug}/${goalInfo.uid}/subscribe`,
      {},
      'topics'
    );
  };
  const PreSubscriptionButton = (
    <FeedButton
      label={
        partPaymentStatus?.is_active_part_set
          ? 'Complete payment'
          : `${isSubscriptionExpired ? 'Renew' : 'Get'} subscription`
      }
      onClick={getOrRenewSubscrioption}
    />
  );
  return (
    <Banner
      imageProps={imageProps}
      title={title}
      content={content}
      feedButton={PreSubscriptionButton}
    />
  );
};

PreSubscriptionBanner.propTypes = {
  goalUID: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  imageProps: PropTypes.objectOf(PropTypes.any).isRequired
};

const PreSubscriptionBannerMemoized = memo(PreSubscriptionBanner);

export default PreSubscriptionBannerMemoized;
